import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { theme } from '../../theme/theme'
import { Modal } from '../modals/Modal'
import { TextInput } from '../inputs/Text'
import { OnClickButton } from '../buttons/onclick/OnClickButton'
import { Title4, BodySmall } from '../styles/Typography'
import { validEmailExp } from '@estercare/ester-shared'
import { useInviteUserMutation } from '../../api/ester-b2b-api/private/users/userQueries'

interface GiftScreeningModalProps {
  onClose: () => void
  onSuccess: (email: string) => void
}

export const GiftScreeningModal = ({ onClose, onSuccess }: GiftScreeningModalProps) => {
  const { t } = useTranslation()
  const { mutateAsync: inviteUser, isPending } = useInviteUserMutation()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState<string>()

  const isEmailValid = validEmailExp.test(email.trim())
  const isFormValid = firstName.trim() && lastName.trim() && isEmailValid

  const handleSubmit = async () => {
    if (!isFormValid) return
    setErrorMessage(undefined)

    try {
      await inviteUser({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() })
      onSuccess(email.trim())
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      if (message.toLowerCase().includes('already')) {
        setErrorMessage(t('employee.screening.giftAlreadyInvited'))
      } else {
        setErrorMessage(message || t('employee.screening.giftError'))
      }
    }
  }

  return (
    <Modal onClose={onClose}>
      <ModalContent>
        <ModalTitle>{t('employee.screening.giftModalTitle')}</ModalTitle>
        <ModalDescription>{t('employee.screening.giftModalDescription')}</ModalDescription>

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <FormContainer>
          <TextInput
            label={t('employee.screening.giftFirstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            backgroundColor={theme.color.beigeLight}
          />
          <TextInput
            label={t('employee.screening.giftLastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            backgroundColor={theme.color.beigeLight}
          />
        </FormContainer>

        <EmailField>
          <TextInput
            label={t('employee.screening.giftEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            backgroundColor={theme.color.beigeLight}
            type="email"
          />
        </EmailField>

        <OnClickButton
          text={t('employee.screening.giftSendButton')}
          onClick={handleSubmit}
          buttonColor={theme.color.plumMid}
          disabled={!isFormValid}
          loading={isPending}
          buttonStyle={{ width: '100%', marginTop: `${theme.spacing.medium}px` }}
        />
      </ModalContent>
    </Modal>
  )
}

const ModalContent = styled.div`
  width: 468px;
  text-align: center;
  @media screen and (max-width: ${theme.breakpoint.small}px) {
    width: 100%;
  }
`

const ModalTitle = styled(Title4)`
  margin-bottom: ${theme.spacing.xsmall}px;
`

const ModalDescription = styled(BodySmall)`
  white-space: pre-line;
`

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${theme.spacing.medium}px;
  margin: ${theme.spacing.large}px 0 ${theme.spacing.medium}px;
  text-align: left;
`

const EmailField = styled.div`
  text-align: left;
`

const ErrorMessage = styled(BodySmall)`
  color: ${theme.color.red};
  margin: ${theme.spacing.small}px 0;
`
